import { useLayoutEffect } from 'react';

/**
 * L'altezza VERA di un elemento, detta al CSS come variabile su `:root`.
 *
 * Il motore condiviso di `useAltezzaBarra` (la barra in basso) e di
 * `useAltezzaBandaAzioni` (le bande di bottoni in fondo alle pagine). Esiste
 * perche' la domanda e' la stessa: un elemento fisso in fondo allo schermo ha
 * un'altezza che cambia — la lingua che manda un'etichetta a capo, la rotazione,
 * il web font che arriva dopo il primo render — e chi gli si deve posare sopra
 * con un numero scritto a mano sbaglia proprio nelle lingue in cui quell'altezza
 * cresce. Due copie di questo meccanismo sarebbero due occasioni perche' una
 * delle due resti indietro.
 *
 * Prende l'**elemento**, non un `RefObject`: i `ref` sono stabili per
 * definizione, quindi con un ref l'effetto non rigirerebbe quando il ramo che
 * disegna l'elemento viene scambiato con un altro — e la variabile resterebbe
 * all'altezza di quello vecchio. Chi chiama tiene l'elemento in uno stato, con
 * un ref di callback. (La barra in basso non ha questo problema — e' una sola e
 * sta sempre li' — ma non vale la pena tenere due firme.)
 *
 * Non si pubblica mai uno zero: elemento assente, ramo spento, nodo staccato o
 * jsdom danno `0`, e scriverlo incollerebbe chi legge al fondo dello schermo.
 * In quel caso la variabile si TOGLIE, e torna a valere il ripiego dichiarato
 * in `index.css` — sbagliato per poco, mai per tutto. E' anche quello che fa
 * respirare da se' chi somma questa misura: a banda spenta il termine vale 0px
 * senza che nessuno debba ricordarsene.
 *
 * @param elemento l'elemento da misurare, o `null` quando non c'e'
 * @param variabile il nome della variabile su `:root` (`--…`)
 */
export function useAltezzaPubblicata(elemento: HTMLElement | null, variabile: string): void {
  useLayoutEffect(() => {
    const radice = document.documentElement;
    if (!elemento) {
      radice.style.removeProperty(variabile);
      return;
    }

    const misura = () => {
      const altezza = elemento.getBoundingClientRect().height;
      if (altezza <= 0) {
        radice.style.removeProperty(variabile);
        return;
      }
      radice.style.setProperty(variabile, `${altezza}px`);
    };

    misura();

    // jsdom non ha il ResizeObserver: la misura sincrona qui sopra e' comunque
    // gia' andata. Stessa guardia di label-fit.ts.
    if (typeof ResizeObserver === 'undefined') {
      return () => radice.style.removeProperty(variabile);
    }
    /*
     * Si osserva l'elemento, non la finestra: il suo box cambia per la
     * rotazione, ma anche per cose che la finestra non vede — la lingua che
     * cambia e manda un'etichetta a capo, il web font che arriva dopo il primo
     * render e sposta la riga di testo.
     */
    const osservatore = new ResizeObserver(misura);
    osservatore.observe(elemento);
    return () => {
      osservatore.disconnect();
      radice.style.removeProperty(variabile);
    };
  }, [elemento, variabile]);
}
